import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Edit, Trash2, DollarSign, Percent, Calendar, TrendingUp, RefreshCw, AlertTriangle, Building } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PricingManager() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [rules, setRules] = useState([]);
  const [occupancyRules, setOccupancyRules] = useState([]);
  const [occupancyData, setOccupancyData] = useState(null);
  const [manualOverride, setManualOverride] = useState({ enabled: false });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [occDialogOpen, setOccDialogOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editingOccRule, setEditingOccRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rule_type: 'date_range',
    start_date: '',
    end_date: '',
    multiplier: 1.0,
    fixed_rate: '',
    scope: 'all',
    scope_value: '',
    priority: 0
  });
  const [occFormData, setOccFormData] = useState({
    name: '',
    description: '',
    occupancy_threshold: '',
    adr_threshold: '',
    operator: 'AND',
    adjustment_type: 'percentage',
    adjustment_value: '',
    applies_to: 'all',
    target_id: '',
    priority: 0
  });
  const [overrideFormData, setOverrideFormData] = useState({
    enabled: false,
    manual_occupancy: '',
    manual_adr: '',
    reason: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchRules();
      fetchOccupancyRules();
      fetchOccupancyData();
      fetchManualOverride();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/pricing-rules?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data);
    } catch (error) {
      toast.error('Failed to load pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchOccupancyRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/occupancy-pricing-rules?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOccupancyRules(response.data);
    } catch (error) {
      console.error('Failed to load occupancy rules:', error);
    }
  };

  const fetchOccupancyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/occupancy-data?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOccupancyData(response.data);
    } catch (error) {
      console.error('Failed to load occupancy data:', error);
    }
  };

  const fetchManualOverride = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/manual-pricing-override?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManualOverride(response.data);
      setOverrideFormData({
        enabled: response.data.enabled || false,
        manual_occupancy: response.data.manual_occupancy || '',
        manual_adr: response.data.manual_adr || '',
        reason: response.data.reason || ''
      });
    } catch (error) {
      console.error('Failed to load manual override:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        property_id: selectedProperty,
        name: formData.name,
        rule_type: formData.rule_type,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        multiplier: formData.fixed_rate ? null : parseFloat(formData.multiplier),
        fixed_rate: formData.fixed_rate ? parseFloat(formData.fixed_rate) : null,
        scope: formData.scope,
        scope_value: formData.scope_value || null,
        priority: parseInt(formData.priority),
        active: true
      };

      if (editingRule) {
        await axios.put(`${API_URL}/pricing-rules/${editingRule.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pricing rule updated');
      } else {
        await axios.post(`${API_URL}/pricing-rules`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pricing rule created');
      }

      setDialogOpen(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error('Failed to save pricing rule');
    }
  };

  const handleOccSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        property_id: selectedProperty,
        name: occFormData.name,
        description: occFormData.description,
        occupancy_threshold: occFormData.occupancy_threshold ? parseFloat(occFormData.occupancy_threshold) : null,
        adr_threshold: occFormData.adr_threshold ? parseFloat(occFormData.adr_threshold) : null,
        operator: occFormData.operator,
        adjustment_type: occFormData.adjustment_type,
        adjustment_value: parseFloat(occFormData.adjustment_value),
        applies_to: occFormData.applies_to,
        target_id: occFormData.target_id || null,
        priority: parseInt(occFormData.priority),
        active: true
      };

      if (editingOccRule) {
        await axios.put(`${API_URL}/occupancy-pricing-rules/${editingOccRule.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Occupancy rule updated');
      } else {
        await axios.post(`${API_URL}/occupancy-pricing-rules`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Occupancy rule created');
      }

      setOccDialogOpen(false);
      setEditingOccRule(null);
      resetOccForm();
      fetchOccupancyRules();
    } catch (error) {
      toast.error('Failed to save occupancy rule');
    }
  };

  const handleOverrideSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/manual-pricing-override`, {
        property_id: selectedProperty,
        enabled: overrideFormData.enabled,
        manual_occupancy: overrideFormData.manual_occupancy ? parseFloat(overrideFormData.manual_occupancy) : null,
        manual_adr: overrideFormData.manual_adr ? parseFloat(overrideFormData.manual_adr) : null,
        reason: overrideFormData.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Manual override updated');
      setOverrideDialogOpen(false);
      fetchOccupancyData();
      fetchManualOverride();
    } catch (error) {
      toast.error('Failed to update manual override');
    }
  };

  const handleRefreshOccupancy = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/occupancy-data/refresh?property_id=${selectedProperty}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Occupancy data refreshed from OHIP');
      fetchOccupancyData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to refresh occupancy data';
      toast.error(errorMessage);
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/pricing-rules/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const deleteOccRule = async (id) => {
    if (!window.confirm('Delete this occupancy rule?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/occupancy-pricing-rules/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Occupancy rule deleted');
      fetchOccupancyRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rule_type: 'date_range',
      start_date: '',
      end_date: '',
      multiplier: 1.0,
      fixed_rate: '',
      scope: 'all',
      scope_value: '',
      priority: 0
    });
  };

  const resetOccForm = () => {
    setOccFormData({
      name: '',
      description: '',
      occupancy_threshold: '',
      adr_threshold: '',
      operator: 'AND',
      adjustment_type: 'percentage',
      adjustment_value: '',
      applies_to: 'all',
      target_id: '',
      priority: 0
    });
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      start_date: rule.start_date ? rule.start_date.split('T')[0] : '',
      end_date: rule.end_date ? rule.end_date.split('T')[0] : '',
      multiplier: rule.multiplier || 1.0,
      fixed_rate: rule.fixed_rate || '',
      scope: rule.scope || 'all',
      scope_value: rule.scope_value || '',
      priority: rule.priority || 0
    });
    setDialogOpen(true);
  };

  const openEditOccDialog = (rule) => {
    setEditingOccRule(rule);
    setOccFormData({
      name: rule.name,
      description: rule.description || '',
      occupancy_threshold: rule.occupancy_threshold || '',
      adr_threshold: rule.adr_threshold || '',
      operator: rule.operator || 'AND',
      adjustment_type: rule.adjustment_type || 'percentage',
      adjustment_value: rule.adjustment_value || '',
      applies_to: rule.applies_to || 'all',
      target_id: rule.target_id || '',
      priority: rule.priority || 0
    });
    setOccDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#1C1917]">Dynamic Pricing</h1>
          <p className="text-[#78716C] mt-1">Manage date-based and occupancy-driven pricing rules</p>
        </div>
        <select
          value={selectedProperty}
          onChange={(e) => setSelectedProperty(e.target.value)}
          className="px-4 py-2 border border-[#E7E5E4] rounded-sm bg-white"
        >
          {properties.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <Tabs defaultValue="date-rules" className="space-y-6">
        <TabsList className="bg-[#F5F5F4] p-1">
          <TabsTrigger value="date-rules" className="data-[state=active]:bg-white">
            <Calendar className="w-4 h-4 mr-2" />
            Date-Based Rules
          </TabsTrigger>
          <TabsTrigger value="occupancy-rules" className="data-[state=active]:bg-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Occupancy/ADR Rules
          </TabsTrigger>
        </TabsList>

        {/* Date-Based Rules Tab */}
        <TabsContent value="date-rules">
          <Card className="border-[#E7E5E4]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl font-serif">Date-Based Pricing Rules</CardTitle>
                <CardDescription>Apply price adjustments for specific date ranges</CardDescription>
              </div>
              <Button
                onClick={() => { resetForm(); setEditingRule(null); setDialogOpen(true); }}
                className="bg-[#9F1239] hover:bg-[#881337]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#9F1239]" />
                </div>
              ) : rules.length === 0 ? (
                <p className="text-center text-[#78716C] py-8">No pricing rules configured</p>
              ) : (
                <div className="space-y-3">
                  {rules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between p-4 border border-[#E7E5E4] rounded-sm hover:bg-[#FAFAF9]"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-[#1C1917]">{rule.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded ${rule.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {rule.active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-[#F5F5F4] text-[#78716C] rounded">
                            Priority: {rule.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-[#78716C]">
                          {rule.start_date && rule.end_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(rule.start_date), 'MMM d')} - {format(new Date(rule.end_date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {rule.multiplier && (
                            <span className="flex items-center gap-1">
                              <Percent className="w-3 h-3" />
                              {rule.multiplier}x multiplier
                            </span>
                          )}
                          {rule.fixed_rate && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              ${rule.fixed_rate} fixed
                            </span>
                          )}
                          {rule.scope !== 'all' && (
                            <span className="text-xs">Scope: {rule.scope} = {rule.scope_value}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(rule)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteRule(rule.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Occupancy/ADR Rules Tab */}
        <TabsContent value="occupancy-rules" className="space-y-6">
          {/* Current Occupancy Data Card */}
          <Card className="border-[#E7E5E4]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#9F1239]" />
                  Current Hotel Data
                </CardTitle>
                <CardDescription>
                  {occupancyData?.is_override ? (
                    <span className="text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Using manual override values
                    </span>
                  ) : (
                    'Live data from OHIP (refreshed every 2 hours)'
                  )}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleRefreshOccupancy}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button 
                  variant={manualOverride.enabled ? "destructive" : "outline"} 
                  size="sm" 
                  onClick={() => setOverrideDialogOpen(true)}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {manualOverride.enabled ? 'Override Active' : 'Manual Override'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#F5F5F4] p-4 rounded-sm">
                  <p className="text-xs text-[#78716C] uppercase tracking-wide">Occupancy</p>
                  <p className="text-2xl font-bold text-[#1C1917]">
                    {occupancyData?.occupancy_percentage != null ? `${occupancyData.occupancy_percentage}%` : '--'}
                  </p>
                </div>
                <div className="bg-[#F5F5F4] p-4 rounded-sm">
                  <p className="text-xs text-[#78716C] uppercase tracking-wide">ADR</p>
                  <p className="text-2xl font-bold text-[#1C1917]">
                    {occupancyData?.adr != null ? `$${occupancyData.adr.toFixed(2)}` : '--'}
                  </p>
                </div>
                <div className="bg-[#F5F5F4] p-4 rounded-sm">
                  <p className="text-xs text-[#78716C] uppercase tracking-wide">Data Source</p>
                  <p className="text-sm font-medium text-[#1C1917]">
                    {occupancyData?.source === 'manual_override' ? 'Manual' : occupancyData?.source || '--'}
                  </p>
                </div>
                <div className="bg-[#F5F5F4] p-4 rounded-sm">
                  <p className="text-xs text-[#78716C] uppercase tracking-wide">Last Updated</p>
                  <p className="text-sm font-medium text-[#1C1917]">
                    {occupancyData?.fetched_at ? format(new Date(occupancyData.fetched_at), 'MMM d, h:mm a') : '--'}
                  </p>
                </div>
              </div>
              {occupancyData?.reason && (
                <p className="mt-3 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                  Override reason: {occupancyData.reason}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Occupancy Rules List */}
          <Card className="border-[#E7E5E4]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl font-serif">Occupancy/ADR Pricing Rules</CardTitle>
                <CardDescription>Automatically adjust prices based on hotel occupancy and ADR thresholds</CardDescription>
              </div>
              <Button
                onClick={() => { resetOccForm(); setEditingOccRule(null); setOccDialogOpen(true); }}
                className="bg-[#9F1239] hover:bg-[#881337]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </CardHeader>
            <CardContent>
              {occupancyRules.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-[#D6D3D1] mx-auto mb-3" />
                  <p className="text-[#78716C]">No occupancy rules configured</p>
                  <p className="text-sm text-[#A8A29E] mt-1">
                    Create rules to automatically increase prices during high-demand periods
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {occupancyRules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between p-4 border border-[#E7E5E4] rounded-sm hover:bg-[#FAFAF9]"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-[#1C1917]">{rule.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded ${rule.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {rule.active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-[#F5F5F4] text-[#78716C] rounded">
                            Priority: {rule.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-sm">
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                            IF {rule.occupancy_threshold != null && `Occupancy >= ${rule.occupancy_threshold}%`}
                            {rule.occupancy_threshold != null && rule.adr_threshold != null && ` ${rule.operator} `}
                            {rule.adr_threshold != null && `ADR >= $${rule.adr_threshold}`}
                          </span>
                          <span className="text-[#78716C]">THEN</span>
                          <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs">
                            +{rule.adjustment_value}{rule.adjustment_type === 'percentage' ? '%' : ' $'}
                          </span>
                        </div>
                        {rule.description && (
                          <p className="text-xs text-[#A8A29E] mt-1">{rule.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditOccDialog(rule)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteOccRule(rule.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* How It Works */}
          <Card className="bg-[#FAFAF9] border-[#E7E5E4]">
            <CardContent className="p-6">
              <h4 className="font-serif font-medium text-[#9F1239] mb-3">How Occupancy Pricing Works</h4>
              <ul className="text-sm text-[#78716C] space-y-2">
                <li>&#8226; <strong>Data Source:</strong> Occupancy and ADR are fetched from OHIP every 2 hours and cached.</li>
                <li>&#8226; <strong>Thresholds:</strong> Set conditions like &quot;IF occupancy &gt;= 90% AND ADR &gt;= $500&quot;.</li>
                <li>&#8226; <strong>Operators:</strong> Use AND (both conditions) or OR (either condition) between thresholds.</li>
                <li>&#8226; <strong>Adjustments:</strong> Apply a percentage increase or flat dollar amount to base prices.</li>
                <li>&#8226; <strong>Priority:</strong> Higher priority rules are evaluated first. Rules stack additively.</li>
                <li>&#8226; <strong>Manual Override:</strong> When OHIP is unavailable, staff can manually set occupancy/ADR values.</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Date Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              {editingRule ? 'Edit Pricing Rule' : 'Create Pricing Rule'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Rule Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Holiday Peak Season"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="multiplier">Multiplier (e.g., 1.5 = +50%)</Label>
                <Input
                  id="multiplier"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.multiplier}
                  onChange={(e) => setFormData({ ...formData, multiplier: e.target.value })}
                  disabled={!!formData.fixed_rate}
                />
              </div>
              <div>
                <Label htmlFor="fixed_rate">OR Fixed Rate ($)</Label>
                <Input
                  id="fixed_rate"
                  type="number"
                  step="0.01"
                  value={formData.fixed_rate}
                  onChange={(e) => setFormData({ ...formData, fixed_rate: e.target.value })}
                  placeholder="Overrides multiplier"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="scope">Scope</Label>
                <select
                  id="scope"
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E7E5E4] rounded-sm"
                >
                  <option value="all">All Inventory</option>
                  <option value="type">By Type</option>
                  <option value="zone">By Zone</option>
                </select>
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Input
                  id="priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                />
              </div>
            </div>
            {formData.scope !== 'all' && (
              <div>
                <Label htmlFor="scope_value">Scope Value</Label>
                <Input
                  id="scope_value"
                  value={formData.scope_value}
                  onChange={(e) => setFormData({ ...formData, scope_value: e.target.value })}
                  placeholder={formData.scope === 'type' ? 'e.g., cabana' : 'e.g., VIP'}
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#9F1239] hover:bg-[#881337]">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Occupancy Rule Dialog */}
      <Dialog open={occDialogOpen} onOpenChange={setOccDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              {editingOccRule ? 'Edit Occupancy Rule' : 'Create Occupancy Rule'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOccSubmit} className="space-y-4">
            <div>
              <Label htmlFor="occ_name">Rule Name</Label>
              <Input
                id="occ_name"
                value={occFormData.name}
                onChange={(e) => setOccFormData({ ...occFormData, name: e.target.value })}
                placeholder="e.g., High Occupancy Surge"
                required
              />
            </div>
            <div>
              <Label htmlFor="occ_desc">Description (optional)</Label>
              <Input
                id="occ_desc"
                value={occFormData.description}
                onChange={(e) => setOccFormData({ ...occFormData, description: e.target.value })}
                placeholder="e.g., Increase prices when hotel is nearly full"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="occ_threshold">Occupancy Threshold (%)</Label>
                <Input
                  id="occ_threshold"
                  type="number"
                  min="0"
                  max="100"
                  value={occFormData.occupancy_threshold}
                  onChange={(e) => setOccFormData({ ...occFormData, occupancy_threshold: e.target.value })}
                  placeholder="e.g., 90"
                />
              </div>
              <div>
                <Label htmlFor="occ_operator">Operator</Label>
                <select
                  id="occ_operator"
                  value={occFormData.operator}
                  onChange={(e) => setOccFormData({ ...occFormData, operator: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E7E5E4] rounded-sm"
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
              </div>
              <div>
                <Label htmlFor="adr_threshold">ADR Threshold ($)</Label>
                <Input
                  id="adr_threshold"
                  type="number"
                  min="0"
                  value={occFormData.adr_threshold}
                  onChange={(e) => setOccFormData({ ...occFormData, adr_threshold: e.target.value })}
                  placeholder="e.g., 500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="adj_type">Adjustment Type</Label>
                <select
                  id="adj_type"
                  value={occFormData.adjustment_type}
                  onChange={(e) => setOccFormData({ ...occFormData, adjustment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E7E5E4] rounded-sm"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount ($)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="adj_value">Adjustment Value</Label>
                <Input
                  id="adj_value"
                  type="number"
                  step="0.01"
                  value={occFormData.adjustment_value}
                  onChange={(e) => setOccFormData({ ...occFormData, adjustment_value: e.target.value })}
                  placeholder={occFormData.adjustment_type === 'percentage' ? 'e.g., 25' : 'e.g., 50'}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="occ_priority">Priority (higher = evaluated first)</Label>
              <Input
                id="occ_priority"
                type="number"
                value={occFormData.priority}
                onChange={(e) => setOccFormData({ ...occFormData, priority: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setOccDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#9F1239] hover:bg-[#881337]">
                {editingOccRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manual Override Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Manual Pricing Override</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOverrideSubmit} className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-800">
                Enable this when OHIP data is unavailable or incorrect. Manual values will override live data.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="override_enabled"
                checked={overrideFormData.enabled}
                onChange={(e) => setOverrideFormData({ ...overrideFormData, enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="override_enabled">Enable Manual Override</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="manual_occ">Occupancy (%)</Label>
                <Input
                  id="manual_occ"
                  type="number"
                  min="0"
                  max="100"
                  value={overrideFormData.manual_occupancy}
                  onChange={(e) => setOverrideFormData({ ...overrideFormData, manual_occupancy: e.target.value })}
                  placeholder="e.g., 95"
                  disabled={!overrideFormData.enabled}
                />
              </div>
              <div>
                <Label htmlFor="manual_adr">ADR ($)</Label>
                <Input
                  id="manual_adr"
                  type="number"
                  min="0"
                  value={overrideFormData.manual_adr}
                  onChange={(e) => setOverrideFormData({ ...overrideFormData, manual_adr: e.target.value })}
                  placeholder="e.g., 600"
                  disabled={!overrideFormData.enabled}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="override_reason">Reason (optional)</Label>
              <Input
                id="override_reason"
                value={overrideFormData.reason}
                onChange={(e) => setOverrideFormData({ ...overrideFormData, reason: e.target.value })}
                placeholder="e.g., OHIP API unavailable"
                disabled={!overrideFormData.enabled}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setOverrideDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#9F1239] hover:bg-[#881337]">
                Save Override
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
